import { isEqual } from 'lodash';
import { isDeepEqual } from 'utils/generic';

export function isRowCollapsed(node) {
  if (node.level < 1) {
    return false;
  }

  if (!node.parent.expanded) {
    return true;
  }

  return isRowCollapsed(node.parent);
}

/**
 * Returns a flag marking if node is part of parent's path
 *
 * NOTE: Will return true if parent and node are the same row
 */

export function isDescendantOf(parent, node) {
  if (node.level < 1) {
    return false;
  }

  if (!node.data.groupPath) {
    return false;
  }

  return node.data.groupPath.includes(parent.key);
}

export function recursivelyCollapse(node) {
  const updatedNodes = [];
  node.childrenAfterFilter.forEach((n) => {
    if (n.data.group) {
      n.expanded = false;
      updatedNodes.push(n, ...recursivelyCollapse(n));
    }
  });

  return updatedNodes;
}

function isEqualColIds(columnState, agGridColumnState) {
  const columnStateColIds =
    columnState && columnState.length
      ? columnState.map((column) => column.colId)
      : [];
  const agGridColumnStateColIds =
    agGridColumnState && agGridColumnState.length
      ? agGridColumnState.map((column) => column.colId)
      : [];

  return isEqual(columnStateColIds, agGridColumnStateColIds);
}

export function gridComponentDidUpdate(
  colState,
  setColumnState,
  setGroupState,
  path,
  grid,
  columnApi
) {
  const { columnState, groupState } =
    (colState && colState[path] && colState[path][grid]) || {};

  columnApi && columnApi.resetColumnState();
  const agGridColumnState = columnApi && columnApi.getColumnState();
  const agGridGroupState = columnApi && columnApi.getColumnGroupState();

  if (agGridColumnState && columnState) {
    refreshColumnState({ columnApi, agGridColumnState, columnState });
  }

  if (groupState && !isDeepEqual(groupState, agGridGroupState)) {
    columnApi && columnApi.setColumnGroupState(groupState);
  }

  // in case we load old CF file without gridState we have to save ag-grid column and group state to store
  if (!columnState && agGridColumnState) {
    setColumnState(path, agGridColumnState);

    if (!groupState && agGridGroupState) {
      setGroupState(path, agGridGroupState);
    }
  }
}

export function handleGridChange(
  path,
  colState,
  setColumnState,
  setGroupState,
  grid,
  columnApi
) {
  const { columnState, groupState } =
    (colState && colState[path] && colState[path][grid]) || {};

  const agGridColumnState = columnApi.getColumnState();
  const agGridGroupState = columnApi.getColumnGroupState();

  if (!isDeepEqual(columnState, agGridColumnState)) {
    setColumnState(path, agGridColumnState);
  }

  if (!isDeepEqual(groupState, agGridGroupState)) {
    setGroupState(path, agGridGroupState);
  }
}

export function handleGridReady(path, colState, grid, api, columnApi) {
  const { columnState, groupState } =
    (colState && colState[path] && colState[path][grid]) || {};

  columnApi && columnApi.resetColumnState();
  const agGridColumnState = columnApi.getColumnState();
  if (agGridColumnState && columnState) {
    refreshColumnState({ columnApi, agGridColumnState, columnState });
  }

  const equalColIds = isEqualColIds(columnState, agGridColumnState);
  if (!columnState) {
    api.sizeColumnsToFit();
  }
  if (groupState && equalColIds) {
    columnApi.setColumnGroupState(groupState);
  }
}

function refreshColumnState({ columnApi, agGridColumnState, columnState }) {
  const newColumnState = [];

  const hasEqualColIds = isEqualColIds(columnState, agGridColumnState);
  const hasEqualNumberOfCols = isEqualNumberOfCols(
    columnState,
    agGridColumnState
  );

  if (!hasEqualColIds && hasEqualNumberOfCols) {
    agGridColumnState.forEach((agGridColumn, index) => {
      const columnStateColumnByIndex = columnState[index];
      const agGridColumnByIndex = agGridColumnState[index];
      columnStateColumnByIndex.colId = agGridColumnByIndex.colId;
      newColumnState.push(columnStateColumnByIndex);
    });
  } else {
    agGridColumnState.forEach((agGridColumn) => {
      const columnStateColumn = columnState.find(
        (column) => column.colId === agGridColumn.colId
      );
      const hasColInColumnState = columnStateColumn !== undefined;
      if (hasColInColumnState) {
        newColumnState.push(columnStateColumn);
      } else {
        newColumnState.push(agGridColumn);
      }
    });
  }

  columnApi && columnApi.setColumnState(newColumnState);
}

function isEqualNumberOfCols(columnState, agGridColumnState) {
  return columnState.length === agGridColumnState.length;
}
